/**
 * useConvexProfile - Convex-based profile hook
 *
 * This hook provides user profile data using Convex real-time subscriptions,
 * replacing REST API calls to /api/v1/user-profile.
 */

import { useQuery, useMutation } from "convex/react";
import { api } from "@careercard/convex/convex/_generated/api";
import type { Doc } from "@careercard/convex/convex/_generated/dataModel";
import { useMemo, useCallback } from "react";
import { useAuthState } from "../useAuthState";

// =============================================================================
// Types
// =============================================================================

/**
 * Profile response from Convex (matches ProfileResponse in profile.ts)
 */
export interface ConvexProfileResponse {
  id: string;
  email: string;
  fullName?: string;
  profileImageUrl?: string;
  userType?: string;
  onboardingComplete?: boolean;
  firstName?: string;
  lastName?: string;
  location?: string;
  professionalSummary?: string;
  careerObjectives?: string;
  skills?: string[];
  jobTitle?: string;
  company?: string;
  createdAt: number;
  updatedAt: number;
}

/**
 * Legacy format for compatibility with existing Profile page
 */
export interface LegacyProfileFormat {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  location: string | null;
  professional_summary: string | null;
  skills: string[] | null;
  career_objectives: string | null;
  job_title: string | null;
  company: string | null;
  created_at: string;
  updated_at: string;
  onboarding_complete: boolean;
  user_type: string;
}

/**
 * Transform Convex profile to legacy format for backward compatibility
 */
function toLegacyFormat(profile: ConvexProfileResponse): LegacyProfileFormat {
  return {
    id: profile.id,
    email: profile.email,
    first_name: profile.firstName ?? null,
    last_name: profile.lastName ?? null,
    location: profile.location ?? null,
    professional_summary: profile.professionalSummary ?? null,
    skills: profile.skills ?? null,
    career_objectives: profile.careerObjectives ?? null,
    job_title: profile.jobTitle ?? null,
    company: profile.company ?? null,
    created_at: new Date(profile.createdAt).toISOString(),
    updated_at: new Date(profile.updatedAt).toISOString(),
    onboarding_complete: profile.onboardingComplete ?? false,
    user_type: profile.userType ?? "professional",
  };
}

// =============================================================================
// Hook Implementation
// =============================================================================

export interface UseConvexProfileReturn {
  /** Profile data in Convex format */
  profile: ConvexProfileResponse | null;
  /** Profile data in legacy format for backward compatibility */
  legacyProfile: LegacyProfileFormat | null;
  /** Whether data is loading */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Update the profile */
  updateProfile: (data: {
    firstName?: string;
    lastName?: string;
    location?: string;
    professionalSummary?: string;
    careerObjectives?: string;
    skills?: string[];
    jobTitle?: string;
    company?: string;
  }) => Promise<ConvexProfileResponse>;
}

/**
 * Hook for fetching and updating user profile via Convex.
 *
 * @returns Profile data and mutations
 *
 * @example
 * ```tsx
 * const { legacyProfile, loading, updateProfile } = useConvexProfile();
 *
 * if (loading) return <Spinner />;
 *
 * const handleSave = async () => {
 *   await updateProfile({
 *     firstName: 'John',
 *     lastName: 'Doe',
 *   });
 * };
 * ```
 */
export function useConvexProfile(): UseConvexProfileReturn {
  const { isAuthenticated } = useAuthState();

  // Real-time subscription to profile - skip if not authenticated
  const profileQuery = useQuery(
    api.profile.getProfile,
    isAuthenticated ? {} : "skip"
  );
  const updateMutation = useMutation(api.profile.updateProfile);

  const profile = useMemo(
    () => (profileQuery as ConvexProfileResponse | null) ?? null,
    [profileQuery]
  );

  const legacyProfile = useMemo(
    () => (profile ? toLegacyFormat(profile) : null),
    [profile]
  );

  const loading = profileQuery === undefined;

  const updateProfile = useCallback(
    async (data: {
      firstName?: string;
      lastName?: string;
      location?: string;
      professionalSummary?: string;
      careerObjectives?: string;
      skills?: string[];
      jobTitle?: string;
      company?: string;
    }): Promise<ConvexProfileResponse> => {
      const result = await updateMutation(data);
      return result as ConvexProfileResponse;
    },
    [updateMutation]
  );

  return {
    profile,
    legacyProfile,
    loading,
    error: null, // Convex handles errors via error boundaries
    updateProfile,
  };
}

// =============================================================================
// Separate mutation hook (for components that only need mutations)
// =============================================================================

export interface UseConvexProfileMutationsReturn {
  /** Update the profile */
  updateProfile: (data: {
    firstName?: string;
    lastName?: string;
    location?: string;
    professionalSummary?: string;
    careerObjectives?: string;
    skills?: string[];
    jobTitle?: string;
    company?: string;
  }) => Promise<ConvexProfileResponse>;
}

/**
 * Hook for profile mutations only (no subscription).
 */
export function useConvexProfileMutations(): UseConvexProfileMutationsReturn {
  const updateMutation = useMutation(api.profile.updateProfile);

  const updateProfile = useCallback(
    async (data: {
      firstName?: string;
      lastName?: string;
      location?: string;
      professionalSummary?: string;
      careerObjectives?: string;
      skills?: string[];
      jobTitle?: string;
      company?: string;
    }): Promise<ConvexProfileResponse> => {
      const result = await updateMutation(data);
      return result as ConvexProfileResponse;
    },
    [updateMutation]
  );

  return {
    updateProfile,
  };
}
